import { useState, useMemo } from "react";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Input } from "@shared/ui/input";
import { Badge } from "@shared/ui/badge";
import { Plus, Search, Edit, Trash2, MoreHorizontal, Eye, RefreshCw } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@shared/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@shared/ui/table";
import { ComplaintTypeForm } from "./complaintTypeForm";
import { ContextualHelp } from "@shared/ui/contextual-help";
import {
  useAllComplaintConfig,
  useDeleteComplaintConfig,
  useSearchComplaintConfig,
  useToggleServiceConfig,
} from "@features/cx/settings/hooks";
import { toast } from "@shared/hooks/use-toast";
import { useBaseComponent } from "@shared/base/hooks/useBaseComponent";
import { ComplaintTypeViewForm } from "./complaintViewForm";
import { ComplaintType } from "@features/cx/settings/types";

export function ComplaintConfigurationTab() {
  // State management
  const [showForm, setShowForm] = useState(false);
  const [editingType, setEditingType] = useState<ComplaintType | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewingComplaint, setViewingComplaint] =
    useState<ComplaintType | null>(null);
  const [showViewForm, setShowViewForm] = useState(false);
  const updateConfig = useToggleServiceConfig();

  // API hooks
  const {
    data: complaintConfigDetails = [],
    refetch: refetchComplaintConfigDetails,
  } = useAllComplaintConfig(1);

  const { data: searchResults = [] } = useSearchComplaintConfig(1, searchQuery);

  const deleteComplaintConfiguration = useDeleteComplaintConfig();

  // Determine which data to display based on search query
  const displayedComplaints = useMemo(() => {
    return searchQuery.trim() ? searchResults : complaintConfigDetails;
  }, [searchQuery, searchResults, complaintConfigDetails]);

  // Search handler
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    // Optional: Add debouncing here if needed
  };

  const handleEdit = (type: ComplaintType) => {
    setEditingType(type);
    setShowForm(true);
  };

  const handleAddNew = () => {
    setEditingType(null);
    setShowForm(true);
  };

  const handleView = (complaint: ComplaintType) => {
    setViewingComplaint(complaint);
    setShowViewForm(true);
  };

  const handleStatusToggle = (service: any) => {
    const payload = {
      id: service.id,
      isActive: !service.isActive,
    };
    updateConfig.mutateAsync(payload);
    console.log("Toggling status for service:", service);
  };

  // Badge variant helpers
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "Active":
        return "default";
      case "Inactive":
        return "secondary";
      default:
        return "secondary";
    }
  };

  const getSeverityBadgeVariant = (severity: string) => {
    switch (severity) {
      case "Critical":
        return "destructive";
      case "High":
        return "default";
      case "Medium":
        return "secondary";
      case "Low":
        return "outline";
      default:
        return "secondary";
    }
  };

  const deleteComplaintConfig = async (complaint_id: any) => {
    deleteComplaintConfiguration.mutateAsync(complaint_id);
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex justify-between items-start">
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <h2 className="card-heading-text">Complaint Configuration</h2>
            <ContextualHelp
              title="Complaint Types"
              content="Manage specific complaint types within categories. Each type has defined severity levels, SLA requirements, and escalation rules."
            />
          </div>
          <p className="card-subheading-text">
            Enables customization of complaint workflows, priorities, and
            handling rules.
          </p>
        </div>

        <Button onClick={handleAddNew}>
          <Plus className="h-4 w-4 mr-2" />
          Add New Complaint
        </Button>
      </div>

      {/* Search Bar */}

      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="search by complaint name or code..."
            className="pl-8"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          <ContextualHelp
            title="Search Complaint Types"
            content="Search through complaint types by name or code. The search is case-insensitive and will match partial names or codes."
            className="absolute right-2 top-2"
          />
        </div>
      </div>

      {/* Complaint Types Table */}
      <Card>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Complaint Name</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Subcategory</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Priority </TableHead>
                <TableHead>Resolution Time</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayedComplaints.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    {searchQuery
                      ? `No complaint types found matching "${searchQuery}"`
                      : "No complaint types found. Click 'Add New Complaint Type' to create your first type."}
                  </TableCell>
                </TableRow>
              ) : (
                displayedComplaints.map((complaint) => (
                  <TableRow
                    key={complaint.id}
                    className="cursor-pointer hover:bg-muted/50"
                  >
                    <TableCell className="font-medium">
                      {complaint.name}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {complaint.configurationCode}
                    </TableCell>
                    <TableCell>{complaint.supportRequestTypeDisplay}</TableCell>
                    <TableCell>
                      {complaint.supportRequestSubtypeDisplay}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={getStatusBadgeVariant(
                          complaint.isActive ? "Active" : "Inactive"
                        )}
                      >
                        {complaint.isActive === true ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={getSeverityBadgeVariant(
                          complaint.extraData.severityLevelDisplay
                        )}
                      >
                        {complaint.extraData.severityLevelDisplay}
                      </Badge>
                    </TableCell>
                    <TableCell>{complaint.extraData.resolutionTime}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(complaint);
                            }}
                          >
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>

                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStatusToggle(complaint);
                            }}
                          >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            {complaint.isActive ? "Inactivate" : "Activate"}
                          </DropdownMenuItem>

                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleView(complaint);
                            }}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => deleteComplaintConfig(complaint.id)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Complaint Type Form Modal */}
      {showForm && (
        <ComplaintTypeForm
          complaintType={editingType}
          open={showForm}
          onClose={() => {
            setShowForm(false);
            setEditingType(null);
          }}
          onSave={(typeData) => {
            setShowForm(false);
            setEditingType(null);
            refetchComplaintConfigDetails();
          }}
        />
      )}
      {showViewForm && (
        <ComplaintTypeViewForm
          complaintType={viewingComplaint}
          open={showViewForm}
          onClose={() => {
            setShowViewForm(false);
            setViewingComplaint(null);
          }}
          onEdit={() => {
            setShowViewForm(false);
            handleEdit(viewingComplaint);
          }}
        />
      )}
    </div>
  );
}
